
import { Line } from "vue-chartjs";
import Chart from "chart.js";
import moment from "moment";

const TTV_TOOLTIP_POSITION = "ttvAmanDiAreaGrafik";
Chart.Tooltip.positioners[TTV_TOOLTIP_POSITION] = function(
  elements,
  eventPosition
) {
  const area = (this._chart && this._chart.chartArea) || null;

  let x = 0;
  let y = 0;
  let count = 0;

  for (let i = 0; i < elements.length; i++) {
    const el = elements[i];
    if (el && el.hasValue()) {
      const pos = el.tooltipPosition();
      x += pos.x;
      y += pos.y;
      count++;
    }
  }

  if (count > 0) {
    x = x / count;
    y = y / count;
  } else if (eventPosition && isFinite(eventPosition.x)) {
    // tidak ada titik ter-plot pada index ini → pakai posisi kursor
    x = eventPosition.x;
    y = eventPosition.y;
  } else if (area) {
    x = (area.left + area.right) / 2;
    y = (area.top + area.bottom) / 2;
  }

  if (area) {
    // jaga agar tooltip selalu berada di dalam area grafik
    x = Math.min(Math.max(x, area.left), area.right);
    y = Math.min(Math.max(y, area.top), area.bottom);
  }

  return { x: x, y: y };
};

const isNilaiKosong = function(val) {
  return (
    val === null ||
    val === undefined ||
    val === "" ||
    val === "NaN" ||
    (typeof val === "number" && isNaN(val))
  );
};

export default {
  extends: Line,
  props: {
    dataSet: {}
  },
  computed: {
    chartData() {
      return {
        labels: this.dataSet.labels,
        datasets: [
          {
            label: "TD (Sistolik/Diastolik)",
            yAxisID: "A",
            type: "bar",
            data: this.dataSet.tensi,
            barThickness: 6,
            backgroundColor: "rgba(100, 100, 100, 0.7)",
            borderColor: "#333",
            borderWidth: 1,
            order: 5
          },
          {
            label: "Nadi",
            yAxisID: "A",
            data: this.dataSet.nadi,
            pointRadius: 4,
            pointStyle: "circle",
            borderWidth: 2,
            spanGaps: true,
            tension: 0,
            fill: false,
            borderColor: "#FF0000",
            backgroundColor: "#FF0000"
          },
          {
            label: "Suhu",
            yAxisID: "B",
            data: this.dataSet.suhu,
            pointRadius: 4,
            pointStyle: "triangle",
            borderWidth: 2,
            spanGaps: true,
            tension: 0,
            fill: false,
            borderColor: "#0000FF",
            backgroundColor: "#0000FF"
          },
          {
            label: "Respirasi",
            yAxisID: "C",
            data: this.dataSet.nafas,
            pointRadius: 4,
            pointStyle: "rect",
            borderWidth: 2,
            spanGaps: true,
            tension: 0,
            fill: false,
            borderColor: "#000000",
            backgroundColor: "#000000"
          },
          {
            label: "SpO2",
            yAxisID: "D",
            data: this.dataSet.spo2,
            pointRadius: 3,
            pointStyle: "rectRounded",
            borderWidth: 2,
            borderDash: [5, 3],
            spanGaps: true,
            tension: 0,
            fill: false,
            borderColor: "#9C27B0",
            backgroundColor: "#9C27B0"
          }
        ]
      };
    },
    options() {
      return {
        scales: {
          xAxes: [
            {
              ticks: {
                callback: function(value, index, values) {
                  const prevDate =
                    index > 0
                      ? moment(values[index - 1]).format("YYYY-MM-DD")
                      : null;
                  if (
                    index === 0 ||
                    prevDate !== moment(value).format("YYYY-MM-DD")
                  ) {
                    return value;
                  } else {
                    return moment(value).format("HH:mm");
                  }
                },
                maxRotation: 45,
                fontSize: 10
              }
            }
          ],
          yAxes: [
            {
              id: "A",
              scaleLabel: {
                display: true,
                labelString: "TD / Nadi",
                fontColor: "#FF0000"
              },
              display: true,
              position: "left",
              ticks: {
                min: 50,
                max: 300,
                stepSize: 50,
                beginAtZero: false
              }
            },
            {
              id: "B",
              scaleLabel: {
                display: true,
                labelString: "Suhu (\u00B0C)",
                fontColor: "#0000FF"
              },
              display: true,
              position: "right",
              ticks: {
                min: 35,
                max: 40,
                stepSize: 1,
                beginAtZero: false
              },
              gridLines: {
                drawOnChartArea: false
              }
            },
            {
              id: "C",
              scaleLabel: {
                display: true,
                labelString: "Respirasi",
                fontColor: "#000000"
              },
              display: true,
              position: "right",
              ticks: {
                min: 10,
                max: 60,
                stepSize: 10,
                beginAtZero: false
              },
              gridLines: {
                drawOnChartArea: false
              }
            },
            {
              id: "D",
              scaleLabel: {
                display: true,
                labelString: "SpO2 (%)",
                fontColor: "#9C27B0"
              },
              display: false,
              position: "right",
              ticks: {
                min: 80,
                max: 100,
                stepSize: 5,
                beginAtZero: false
              },
              gridLines: {
                drawOnChartArea: false
              }
            }
          ]
        },
        legend: {
          display: true,
          position: "bottom",
          labels: {
            usePointStyle: true,
            padding: 15
          }
        },
        responsive: true,
        maintainAspectRatio: false,
        tooltips: {
          mode: "index",
          intersect: false,
          position: TTV_TOOLTIP_POSITION,
          callbacks: {
            label: function(tooltipItem, data) {
              const label =
                data.datasets[tooltipItem.datasetIndex].label || "";

              if (String(label).toLowerCase().includes("td")) {
                try {
                  const val: Array<number> = JSON.parse(
                    String(tooltipItem.value).replace(/NaN/g, "null")
                  );
                  const terisi = (val || []).filter(v => !isNilaiKosong(v));
                  tooltipItem.value = terisi.length ? terisi.join(" / ") : "-";
                } catch (e) {
                  // keep original value
                }
              }

              const nilai = isNilaiKosong(tooltipItem.value)
                ? "-"
                : tooltipItem.value;

              return `${label} : ${nilai}`;
            }
          }
        },
        plugins: {
          zoom: {
            pan: {
              enabled: true,
              mode: "x"
            },
            zoom: {
              enabled: true,
              mode: "x"
            }
          }
        }
      };
    }
  },

  mounted() {
    this.renderChart(this.chartData, this.options);
  },

  watch: {
    dataSet() {
      this.renderChart(this.chartData, this.options);
    }
  }
};
